import React, { useState, useMemo } from 'react';
import { Dropdown, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import integrationUtils from '../../Utils/integration.utils';
import useDropdown from '../../hooks/dropdown.hook';
import { UpdateTaxRateState } from '../../constants/category.constants';

const MenuActions = {
  EDIT: 'edit',
  MAKE_DEFAULT: 'make_default',
  DISABLE: 'disable'
};

const TaxRateOptionItem = ({
  item,
  selectedTax,
  onChange,
  setUpdatingArea,
  updateTaxRate,
  isLoading,
  updatingArea,
  updateTaxList,
  setTaxRateData,
  setShowEditTaxRate,
}) => {
  const { isOpen, refBtn, refMenu, setIsOpen } = useDropdown({});

  const handleMenuAction = (action, e) => {
    e.preventDefault();

    switch (action) {
      case MenuActions.EDIT:
        setTaxRateData(item);
        setShowEditTaxRate(true);
        setIsOpen(false);
        break;
      case MenuActions.MAKE_DEFAULT:
        setUpdatingArea(UpdateTaxRateState.Default);
        updateTaxRate({
          ...item,
          IsDefault: true,
        }).then(() => {
          setUpdatingArea(UpdateTaxRateState.None);
          setIsOpen(false);
          updateTaxList({ created: item.ObjectId });
        });
        break;
      case MenuActions.DISABLE:
        setUpdatingArea(UpdateTaxRateState.Active);
        updateTaxRate({
          ...item,
          IsDisabled: true,
        }).then(() => {
          setUpdatingArea(UpdateTaxRateState.None);
          setIsOpen(false);
          updateTaxList();
        });
        break;
      default:
        break;
    }
  };

  const menuOptions = useMemo(() => {
    if (item.isImported) {
      return !item?.IsDefault ? [
        {
          id: MenuActions.MAKE_DEFAULT,
          label: 'Make default',
          show: true,
          className: 'px-2 py-1 f-grey d-flex align-items-center justify-content-between bg-hover-grey',
          showLoader: isLoading && updatingArea === UpdateTaxRateState.Default
        }
      ] : [];
    }

    return [
      {
        id: MenuActions.EDIT,
        label: 'Edit',
        show: true,
        className: 'px-2 py-1 f-grey w-100 bg-hover-grey'
      },
      {
        id: MenuActions.MAKE_DEFAULT,
        label: 'Make default',
        show: !item?.IsDefault && item.ObjectId > 0,
        className: 'px-2 py-1 f-grey d-flex align-items-center justify-content-between w-100 bg-hover-grey',
        showLoader: isLoading && updatingArea === UpdateTaxRateState.Default
      },
      {
        id: MenuActions.DISABLE,
        label: 'Disable',
        show: item.ObjectId > 0,
        className: 'px-2 py-1 f-grey d-flex align-items-center justify-content-between w-100 bg-hover-grey',
        showLoader: isLoading && updatingArea === UpdateTaxRateState.Active
      }
    ];
  }, [item, isLoading, updatingArea]);

  return (
    <div
      id={`tax-rate-${item?.ObjectId}`}
      className="d-flex align-items-center justify-content-between radius-4 bg-hover-grey p-2 pointer"
      onClick={(e) => {
        onChange(item);
      }}
    >
      <div
        className="d-flex align-items-center pointer f-grey px-2"
      >
        <img
          src="/images/msp/dropdown-grey-tick.svg"
          className="mr-2"
          alt=""
          style={{
            visibility: `${selectedTax?.ObjectId === item?.ObjectId
                ? "visible"
                : "hidden"
              }`,
          }}
        />
        {item?.TaxName}
      </div>

      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {item?.ObjectId !== 0 && !item.isImported ? (
          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
            {item?.IsDefault && (
              <span className="f-14 f-darkgrey">
                Default
              </span>
            )}
              <Dropdown
                alignRight
                className="transparennt-wrapper-dropdown"
                onToggle={(isOpen) => setIsOpen(isOpen)}
                show={isOpen}
              >
                <Dropdown.Toggle ref={refBtn} className="w-100 f-12 f-500 f-grey">
                  <div className="d-flex align-items-center p-2">
                    <img
                      alt=""
                      src="/images/settings-v2/horizontal-dots.svg"
                      className=""
                    />
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  ref={refMenu}
                  popperConfig={{ strategy: 'fixed' }}
                  alignRight
                  renderOnMount
                >
                  <div className="pointer d-flex flex-column">
                    {menuOptions.map(option => option.show && (
                      <div
                        key={option.id}
                        className={option.className}
                        onClick={(e) => {
                          handleMenuAction(option.id, e);
                        }}
                      >
                        <span>{option.label}</span>
                        {option.showLoader && (
                          <Spinner
                            animation="border"
                            variant="secondary"
                            size="sm"
                            style={{
                              width: '13px',
                              height: '13px',
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </Dropdown.Menu>
              </Dropdown> 
          </div>
        ) : item.isImported ? (
          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
            {item?.IsDefault && (
              <span className="f-14 f-darkgrey">
                Default
              </span>
            )}
            {!item?.IsDefault && (
              <Dropdown
                alignRight
                className="transparennt-wrapper-dropdown"
                onToggle={(isOpen) => setIsOpen(isOpen)}
                show={isOpen}
              >
                <Dropdown.Toggle ref={refBtn} className="w-100 f-12 f-500 f-grey p-0">
                  {!isOpen && (
                    <OverlayTrigger
                      placement="bottom-end"
                      trigger={["hover", "focus"]}
                      delay={{ show: 200, hide: 250 }}
                      container={() => document.getElementById(`tax-rate-${item?.ObjectId}`)}
                      overlay={
                        <Tooltip
                          id={`tax-rate-tooltip`}
                          style={{ position: 'relative', zIndex: 10000 }}
                        >
                          <div className="d-flex" style={{ gap: '4px' }}>
                            <img
                              alt=""
                              src={integrationUtils.getImagePath(item?.IntegrationId)}
                              width="16px"
                              height="16px"
                              style={{
                                borderRadius: '2px',
                                backgroundColor: '#fff',
                                textAlign: 'left',
                                marginTop: '1px'
                              }}
                            />
                            <span>{`Imported tax rate from ${item?.IntegrationName} can not be edited or deleted`}</span>
                          </div>
                        </Tooltip>
                      }
                    >
                      <div className="d-flex align-items-center mx-2">
                        <img
                          alt=""
                          src={integrationUtils.getImagePath(item?.IntegrationId)}
                          width="16px"
                          height="16px"
                          style={{ borderRadius: '2px' }}
                        />
                      </div>
                    </OverlayTrigger>
                  )}
                  {isOpen && (
                    <div className="d-flex align-items-center mx-2">
                      <img
                        alt=""
                        src={integrationUtils.getImagePath(item?.IntegrationId)}
                        width="16px"
                        height="16px"
                        style={{ borderRadius: '2px' }}
                      />
                    </div>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  ref={refMenu}
                  popperConfig={{ strategy: 'fixed' }}
                  alignRight
                  renderOnMount
                >
                  {menuOptions.map(option => option.show && (
                    <div
                      key={option.id}
                      className={option.className}
                      onClick={(e) => {
                        handleMenuAction(option.id, e);
                      }}
                    >
                      <span>{option.label}</span>
                      {option.showLoader && (
                        <Spinner
                          animation="border"
                          variant="secondary"
                          size="sm"
                          style={{
                            width: '13px',
                            height: '13px',
                          }}
                        />
                      )}
                    </div>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
            {item?.IsDefault && (
              <OverlayTrigger
                placement="bottom-end"
                trigger={["hover", "focus"]}
                delay={{ show: 200, hide: 250 }}
                container={() => document.getElementById(`tax-rate-${item?.ObjectId}`)}
                overlay={
                  <Tooltip
                    id={`tax-rate-tooltip`}
                    style={{ position: 'relative', zIndex: 10000 }}
                  >
                    <div className="d-flex" style={{ gap: '4px' }}>
                      <img
                        alt=""
                        src={integrationUtils.getImagePath(item?.IntegrationId)}
                        width="16px"
                        height="16px"
                        style={{
                          borderRadius: '2px',
                          backgroundColor: '#fff',
                          textAlign: 'left',
                          marginTop: '1px'
                        }}
                      />
                      <span>{`Imported tax rate from ${item?.IntegrationName} can not be edited or deleted`}</span>
                    </div>
                  </Tooltip>
                }
              >
                <div className="d-flex align-items-center mx-2">
                  <img
                    alt=""
                    src={integrationUtils.getImagePath(item?.IntegrationId)}
                    width="16px"
                    height="16px"
                    style={{ borderRadius: '2px' }}
                  />
                </div>
              </OverlayTrigger>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TaxRateOptionItem; 